import { useState, useRef, useEffect } from "react";
import { FaSpinner } from "react-icons/fa";
import PrompterDialogLayout from "../layouts/DialogLayout";
import AppNotifications from "./AppNotifications";
import { useAppSelector, useAppDispatch } from "../hooks";
import { setFailed, setError, setSuccess } from "../state/xhr-status/reqStatus";

interface Props {
    onClose: () => void;
}

function DashboardNotetakerModal({ onClose }: Props) {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const reqStatus = useAppSelector((state) => state.reqStatus.value);
    const dispatch = useAppDispatch();

    const [loadingLinkSubmit, setLoadingLinkSubmit] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);  // Success message state
    const [meetingLink, setMeetingLink] = useState("");

    const handleClose = () => {
        if (onClose) onClose(); // Call the onClose callback if provided
    };

    function handleMeetingLinkChange(e: any) {
        setMeetingLink(e.target.value);
    }

    const linkData = {
        meeting_url: meetingLink,
        conversation_id: 0, // Ensure this is properly handled, it can be undefined
    };

    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                modalRef.current &&
                !modalRef.current.contains(event.target as Node)
            ) {
                onClose(); // Close the modal if clicked outside of it
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [onClose]);

    const session = sessionStorage.getItem("tiller") || localStorage.getItem("tiller");
    const access_token = session ? JSON.parse(session).access_token : null;

    async function handleLinkSubmit(e: any) {
        e.preventDefault();

        try {
            setLoadingLinkSubmit(true);
            const response = await fetch(`${apiUrl}/start-call/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${access_token}`,
                },
                body: JSON.stringify(linkData),
            });

            const data = await response.json();
            setLoadingLinkSubmit(false);

            if (response.ok) {
                setSuccessMessage("Notetaker is on its way...");
                setTimeout(() => {
                    setSuccessMessage("Notetaker should be there shortly.");
                }, 8000); 
                setTimeout(() => handleClose(), 15000); 
            } else {
                dispatch(
                    setFailed({
                        msg: data.message,
                        statusCode: response.status,
                    })
                );
            }
        } catch (error) {
            setLoadingLinkSubmit(false);
            dispatch(
                setError({
                    msg: "Something went wrong. Please try again.",
                    statusCode: 500,
                })
            );
        }
    }

    return (
        <PrompterDialogLayout>
            <div ref={modalRef} className="relative">
                {/* Close Button */}
                <button
                    onClick={handleClose}
                    className="absolute top-0 right-0 text-gray-500 hover:text-gray-700"
                    aria-label="Close"
                >
                    &#x2715; {/* Unicode character for "X" */}
                </button>
                <form>
                
                    <h3 className="font-bold text-lg mb-2">
                        Provide a Meeting URL
                    </h3>

                    {reqStatus && <AppNotifications {...reqStatus} />}

                    <p className="text-[--grey-muted] pb-2">
                        Make sure your meeting URL permissions are set so that your Notetaker can join the call.
                    </p>
                    <input
                        onChange={handleMeetingLinkChange}
                        id="meeting-link"
                        className="form-input"
                        placeholder="https://meet.google.com/example"
                        autoComplete="off"
                    />

                    <button
                        onClick={handleLinkSubmit}
                        className={`btn btn-aqua w-full mt-4 ${loadingLinkSubmit
                                ? "cursor-progress bg-[--aqua-dark]"
                                : ""
                            }`}
                        disabled={loadingLinkSubmit || Boolean(successMessage)}
                    >
                        Join call
                        {loadingLinkSubmit && (
                            <FaSpinner className="ml-2 inline animate-spin" />
                        )}
                    </button>

                    {/* Success Message Section */}
                    {successMessage && (
                        <div className="mt-4 text-center text-aqua">
                            <FaSpinner className="inline animate-spin mr-2" />
                            {successMessage}
                        </div>
                    )}

                </form>
            </div>
        </PrompterDialogLayout>
    );
}

export default DashboardNotetakerModal;
